import React from "react";
import { StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";

export interface DurationCellProps {
  /** Duration in hours */
  hours: number | null | undefined;
  /** Show minutes precision for sub-hour durations */
  showMinutes?: boolean;
  /** Placeholder for null values */
  placeholder?: string;
}

/**
 * Format duration in hours to human-readable string
 * Examples:
 *   0.5 → "30m"
 *   2.5 → "2.5h"
 *   26 → "1d 2h"
 *   48 → "2d 0h"
 */
export function formatDuration(hours: number | null | undefined, showMinutes = true): string {
  if (hours == null || isNaN(hours)) return "—";

  // Sub-hour: show minutes
  if (hours < 1 && showMinutes) {
    const minutes = Math.round(hours * 60);
    return `${minutes}m`;
  }

  // Less than a day: show hours with decimal
  if (hours < 24) {
    // If it's a whole number, don't show decimal
    if (hours === Math.floor(hours)) {
      return `${hours}h`;
    }
    return `${hours.toFixed(1)}h`;
  }

  // Days + hours
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);
  return `${days}d ${remainingHours}h`;
}

/**
 * Calculate duration in hours from start/end times
 * Returns numeric hours for sorting
 */
export function calculateDuration(
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined,
): number | null {
  if (!startTime || !endTime) return null;

  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();

  if (isNaN(start) || isNaN(end)) return null;

  return (end - start) / (1000 * 60 * 60); // hours
}

/**
 * Duration cell component - smart formatting for time durations
 * Displays durations in the most readable format based on length
 */
export function DurationCell({ hours, showMinutes = true, placeholder = "—" }: DurationCellProps) {
  const formatted = hours != null ? formatDuration(hours, showMinutes) : placeholder;

  return <ThemedText style={styles.text}>{formatted}</ThemedText>;
}

const styles = StyleSheet.create({
  text: {
    fontSize: 12,
  },
});
